/**
 * PaidMediaDownloadButton — iter238 (Variante B §7, §8)
 *
 * State-Machine-Button für PPV-Käufer:
 *   NONE / PENDING     → „Vorbereitung startet…"  (init aufrufen)
 *   RENDERING          → Progress-Bar mit Prozent
 *   READY + PAID       → „Herunterladen"          (löst DELIVERY_STARTED + DELIVERED aus)
 *   READY + DELIVERED  → „Bereits heruntergeladen"
 *   FAILED             → „Erneut versuchen"
 *   REVOKED            → gesperrt (rechtlicher Takedown)
 *   REFUNDED           → gesperrt (erstattet)
 *
 * Polling: alle 2s wenn RENDERING; sonst kein Poll.
 */
import { useEffect, useRef, useState } from "react";
import { Download, Loader2, CheckCircle, AlertOctagon, RotateCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

const POLL_MS = 2000;

export default function PaidMediaDownloadButton({ purchase, onUpdate }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState({
    status: purchase?.delivery_render_status || "NONE",
    progress: purchase?.delivery_render_progress || 0,
    purchase_state: purchase?.purchase_state,
    delivered_at: purchase?.delivered_at,
  });
  const timerRef = useRef(null);
  // iter242 · Ref hält Cancel-Callback für den 1.5s-Post-Download-Poll,
  // damit ein Unmount kein setState-after-unmount produziert.
  const downloadTimerRef = useRef(null);
  useEffect(() => () => { if (downloadTimerRef.current) { try { downloadTimerRef.current(); } catch (_) {} } }, []);

  useEffect(() => {
    setStatus({
      status: purchase?.delivery_render_status || "NONE",
      progress: purchase?.delivery_render_progress || 0,
      purchase_state: purchase?.purchase_state,
      delivered_at: purchase?.delivered_at,
    });
  }, [purchase?.purchase_id, purchase?.delivery_render_status,
      purchase?.delivery_render_progress, purchase?.purchase_state,
      purchase?.delivered_at]);

  useEffect(() => {
    if (status.status !== "RENDERING" && status.status !== "PENDING") {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return undefined;
    }
    if (timerRef.current) return undefined;
    timerRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/purchases/${purchase.purchase_id}/download-status`);
        setStatus(data);
        onUpdate?.(data);
        if (data.status === "READY" || data.status === "FAILED") {
          clearInterval(timerRef.current); timerRef.current = null;
        }
      } catch { /* silent */ }
    }, POLL_MS);
    return () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
  }, [status.status, purchase?.purchase_id, onUpdate]);

  const state = status.purchase_state;
  const disabled = state === "REVOKED_LEGAL_TAKEDOWN" || state === "REFUNDED";

  const initAndDownload = async () => {
    if (disabled) return;
    setBusy(true); setErr("");
    // iter242 · Cleanup-Ref, damit setTimeout nach Unmount kein setState mehr triggert.
    let cancelled = false;
    downloadTimerRef.current = () => { cancelled = true; };
    try {
      const { data: init } = await api.post(`/purchases/${purchase.purchase_id}/download-init`);
      if (cancelled) return;
      setStatus(init); onUpdate?.(init);
      if (init.status === "READY" && init.delivery_url) {
        await triggerDownload();
        setTimeout(async () => {
          if (cancelled) return;
          try {
            const { data: fresh } = await api.get(`/purchases/${purchase.purchase_id}/download-status`);
            if (cancelled) return;
            setStatus(fresh); onUpdate?.(fresh);
          } catch { /* silent */ }
        }, 1500);
      }
    } catch (e) {
      if (!cancelled) setErr(e?.response?.data?.detail?.message || "Vorbereitung fehlgeschlagen");
    } finally {
      if (!cancelled) setBusy(false);
    }
  };

  const triggerDownload = async () => {
    // iter263b · Download über axios (Blob) statt nativer <a href>-Navigation.
    // Grund: Bei Admin-Impersonation trägt nur axios den Bearer-Token; eine
    // native Navigation sendet ausschließlich das Admin-Cookie → der Endpoint
    // lief als Admin → 404 „Not Found". Blob-Fetch nutzt Bearer (Impersonation)
    // bzw. Cookie (echter Login) korrekt und vermeidet den JSON-Fehler-Tab.
    try {
      const res = await api.get(
        `/purchases/${purchase.purchase_id}/download`,
        { responseType: "blob" },
      );
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/.exec(cd);
      const filename = m ? m[1] : `paidmedia_${String(purchase.purchase_id).slice(0, 8)}`;
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => { try { URL.revokeObjectURL(blobUrl); } catch (_) {} }, 5000);
      setStatus((s) => ({ ...s, purchase_state: "DELIVERED", delivered_at: new Date().toISOString() }));
    } catch (e) {
      if (e?.response?.status === 425) {
        setErr(t("paid_media.download_not_ready", { defaultValue: "Deine Kopie wird noch vorbereitet — bitte kurz warten." }));
      } else {
        setErr(t("paid_media.download_failed", { defaultValue: "Download nicht möglich." }));
      }
    }
  };

  if (disabled) {
    return (
      <button
        type="button"
        data-testid={`download-btn-${purchase.purchase_id}`}
        disabled
        className="h-9 px-4 rounded-full bg-[#F5F5F5] text-[#999] text-xs flex items-center gap-2 cursor-not-allowed"
        title={state === "REVOKED_LEGAL_TAKEDOWN"
          ? "Kauf aus rechtlichen Gründen entzogen"
          : "Kauf wurde erstattet"}
      >
        <AlertOctagon size={12} />
        {state === "REVOKED_LEGAL_TAKEDOWN" ? "Zugriff entzogen" : "Erstattet"}
      </button>
    );
  }

  if (state === "DELIVERED") {
    return (
      <div className="flex flex-col gap-1">
        <button
          type="button"
          data-testid={`download-btn-${purchase.purchase_id}`}
          onClick={initAndDownload}
          className="h-9 px-4 rounded-full border border-[#E5E5E5] text-[#666] text-xs flex items-center gap-2 hover:bg-[#F5F5F5]"
          title={t("paid_media.already_downloaded_resave")}
        >
          <CheckCircle size={12} className="text-green-600" />
          {t("paid_media.download_delivered", { defaultValue: "Erneut speichern" })}
        </button>
        <span className="text-[10px] text-[#999] px-2">
          {t("paid_media.download_delivered_note", {
            defaultValue: "Kopie bereits ausgeliefert.",
          })}
        </span>
      </div>
    );
  }

  if (status.status === "RENDERING" || status.status === "PENDING") {
    // iter244 · Progress bei RENDERING nie auf 100% — sonst suggeriert die
    // Card einen Zustand der noch nicht READY ist. Cap bei 95%.
    const rawProgress = Number(status.progress || 0);
    const shownProgress = Math.min(95, Math.max(5, rawProgress));
    return (
      <div className="flex flex-col gap-1 w-full max-w-[220px]"
           data-testid={`download-status-${purchase.purchase_id}`}>
        <div className="flex items-center gap-2 text-xs text-[#666]">
          <Loader2 size={12} className="animate-spin" />
          <span>
            {t("paid_media.download_preparing", { defaultValue: "Deine Kopie wird vorbereitet" })}
            {" · "}
            {shownProgress}%
          </span>
        </div>
        <div className="w-full h-1.5 rounded-full bg-[#F0F0F0] overflow-hidden">
          <div
            className="h-full bg-[#0A0A0A] transition-all"
            style={{ width: `${shownProgress}%` }}
          />
        </div>
      </div>
    );
  }

  if (status.status === "FAILED") {
    return (
      <button
        type="button"
        data-testid={`download-btn-${purchase.purchase_id}`}
        onClick={initAndDownload}
        disabled={busy}
        className="h-9 px-4 rounded-full border border-red-300 text-red-600 text-xs flex items-center gap-2 hover:bg-red-50"
      >
        <RotateCw size={12} />
        {t("paid_media.download_retry", { defaultValue: "Erneut versuchen" })}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        data-testid={`download-btn-${purchase.purchase_id}`}
        onClick={initAndDownload}
        disabled={busy}
        className="h-9 px-4 rounded-full bg-[#0A0A0A] text-white text-xs flex items-center gap-2 hover:bg-[#222] disabled:opacity-50"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
        {t("paid_media.download_now", { defaultValue: "Herunterladen" })}
      </button>
      {err && <span className="text-[10px] text-red-600 px-2">{err}</span>}
    </div>
  );
}
